
import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { Eye, EyeOff, UserPlus, ChevronRight, Info } from "lucide-react";
import { useAuth } from "@/app/hooks/useAuth";
import { validateSupabaseKeys } from "@/env";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";

const SignupForm = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [gender, setGender] = useState<"male" | "female" | "">("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [supabaseStatus, setSupabaseStatus] = useState<'unknown' | 'connected' | 'error'>('unknown');
  const { signup, supabase } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const testConnection = async () => {
      try {
        console.log("SignupForm: Testing Supabase connection...");
        const keysValid = validateSupabaseKeys();
        if (!keysValid) {
          console.error("SignupForm: Supabase keys validation failed");
          setSupabaseStatus('error');
          return;
        }
        
        console.log("SignupForm: Keys valid, testing database connection...");
        const { data, error } = await supabase.from('ft_profiles').select('count').limit(1);
        
        if (error) {
          console.error("SignupForm: Supabase connection test error:", error);
          console.error("SignupForm: Error details:", {
            code: error.code,
            message: error.message,
            details: error.details,
            hint: error.hint
          });
          setSupabaseStatus('error');
        } else {
          console.log("SignupForm: Supabase connection successful:", data);
          setSupabaseStatus('connected');
        }
      } catch (e) {
        console.error("SignupForm: Supabase connection test exception:", e);
        setSupabaseStatus('error');
      }
    };
    
    testConnection();
  }, [supabase]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const plan = params.get("plan");
    setSelectedPlan(plan);
  }, [location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setErrorMessage("");
    
    console.log("SignupForm: Form submission started with values:", {
      firstName: firstName ? "provided" : "missing",
      lastName: lastName ? "provided" : "missing",
      email: email ? "provided" : "missing",
      gender: gender ? gender : "missing",
      password: password ? `${password.length} chars` : "missing",
      passwordsMatch: password === confirmPassword,
      supabaseStatus
    });
    
    if (!firstName || !lastName) {
      const message = "Please provide your first and last name.";
      console.log("SignupForm: Validation failed -", message);
      setErrorMessage(message);
      toast({
        variant: "destructive",
        title: "Missing information",
        description: message
      });
      return;
    }
    
    if (!email) {
      const message = "Please provide your email address.";
      console.log("SignupForm: Validation failed -", message);
      setErrorMessage(message);
      toast({
        variant: "destructive",
        title: "Missing email",
        description: message
      });
      return;
    }
    
    if (!gender) {
      const message = "Please select your gender.";
      console.log("SignupForm: Validation failed -", message);
      setErrorMessage(message);
      toast({
        variant: "destructive",
        title: "Gender not selected",
        description: message
      });
      return;
    }
    
    if (password !== confirmPassword) {
      const message = "Passwords don't match";
      console.log("SignupForm: Validation failed -", message);
      setErrorMessage(message);
      toast({
        variant: "destructive",
        title: "Passwords don't match",
        description: "Please make sure your passwords match."
      });
      return;
    }
    
    if (password.length < 6) {
      const message = "Password must be at least 6 characters long";
      console.log("SignupForm: Validation failed -", message);
      setErrorMessage(message);
      toast({
        variant: "destructive",
        title: "Password too short",
        description: "Password must be at least 6 characters long."
      });
      return;
    }
    
    if (supabaseStatus === 'error' && email !== "test@example.com") {
      const message = "Unable to connect to our servers. Please try again later.";
      console.log("SignupForm: Validation failed -", message);
      setErrorMessage(message);
      toast({
        variant: "destructive",
        title: "Connection Error",
        description: message
      });
      return;
    }
    
    console.log("SignupForm: Validation passed, proceeding with signup");
    setIsSubmitting(true);
    
    try {
      console.log("SignupForm: Calling signup function with:", { 
        email, 
        firstName, 
        lastName,
        gender,
        passwordLength: password.length 
      });
      
      if (email === "test@example.com") {
        toast({
          title: "Demo account detected",
          description: "Using pre-configured demo account."
        });
      }
      
      await signup(email, password, firstName, lastName, gender);
      
      console.log("SignupForm: Signup flow completed successfully!");
      
      toast({
        title: "Account created!",
        description: "Please check your email to verify your account."
      });
    } catch (error: any) {
      console.error("SignupForm: Signup error:", error);
      const errorMsg = error.message || "An error occurred during signup.";
      setErrorMessage(errorMsg);
      
      toast({
        variant: "destructive",
        title: "Signup failed",
        description: errorMsg
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 bg-white p-8 rounded-xl shadow-md">
      {supabaseStatus === 'error' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4">
          <div className="flex items-center">
            <Info className="h-5 w-5 text-yellow-600 mr-2" />
            <p className="text-sm text-yellow-700">
              Connection issues detected. The signup process may not work correctly.
            </p>
          </div>
        </div>
      )}
      
      {selectedPlan && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
          <p className="text-sm text-blue-700 font-medium">
            You're signing up for the <span className="font-bold capitalize">{selectedPlan}</span> plan
          </p>
        </div>
      )}
      
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}
      
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="firstName" className="text-gray-700">First Name</Label>
          <Input
            id="firstName"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="border-gray-300 focus:border-blue-500 focus:ring-blue-500"
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="lastName" className="text-gray-700">Last Name</Label>
          <Input
            id="lastName"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="border-gray-300 focus:border-blue-500 focus:ring-blue-500"
            required
          />
        </div>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="email" className="text-gray-700">Email</Label>
        <Input
          id="email"
          type="email"
          placeholder="your@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="border-gray-300 focus:border-blue-500 focus:ring-blue-500"
          required
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="gender" className="text-gray-700">Gender</Label>
        <Select value={gender} onValueChange={(value: "male" | "female") => setGender(value)} required>
          <SelectTrigger id="gender" className="w-full">
            <SelectValue placeholder="Select your gender" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="male">Male</SelectItem>
            <SelectItem value="female">Female</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-gray-500">Required for proper avatar display</p>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="password" className="text-gray-700">Password</Label>
        <div className="relative">
          <Input
            id="password"
            type={showPassword ? "text" : "password"}
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="border-gray-300 focus:border-blue-500 focus:ring-blue-500"
            required
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-0 top-0 h-full"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </Button>
        </div>
        <p className="text-xs text-gray-500">Password must be at least 6 characters long</p>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="confirmPassword" className="text-gray-700">Confirm Password</Label>
        <Input
          id="confirmPassword"
          type={showPassword ? "text" : "password"}
          placeholder="••••••••"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          className="border-gray-300 focus:border-blue-500 focus:ring-blue-500"
          required
        />
      </div>
      
      <Button 
        type="submit" 
        className="w-full bg-blue-600 hover:bg-blue-700 text-lg py-6"
        disabled={isSubmitting || supabaseStatus === 'error'}
      >
        {isSubmitting ? (
          <span className="flex items-center justify-center">
            <span className="animate-spin mr-2 h-4 w-4 border-2 border-white border-opacity-50 border-t-transparent rounded-full"></span>
            Creating Account...
          </span>
        ) : (
          <span className="flex items-center justify-center">
            <UserPlus className="mr-2 h-5 w-5" />
            Create Account
            <ChevronRight className="ml-1 h-5 w-5" />
          </span>
        )}
      </Button>
      
      <div className="text-center">
        <p className="text-sm text-gray-600">
          Already have an account?{" "}
          <Link to="/login" className="text-blue-600 font-medium hover:underline">
            Sign in
          </Link>
        </p>
      </div>
      
      <div className="mt-4 pt-4 border-t border-gray-100">
        <p className="text-xs text-gray-500 text-center">
          For testing, use: test@example.com / password123
        </p>
      </div>
    </form>
  );
};

export default SignupForm;
